import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StripeProvider from './StripeProvider';
import CheckoutForm from './CheckoutForm';
import { FaArrowLeft, FaTimes } from 'react-icons/fa';

interface PlanCheckoutProps {
  planId: string;
  planName: string;
  planPrice: number;
  onCancel: () => void;
  isChangingPlan?: boolean;
  onClose?: () => void;
}

const PlanCheckout: React.FC<PlanCheckoutProps> = ({
  planId,
  planName,
  planPrice,
  onCancel,
  isChangingPlan = false,
  onClose,
}) => {
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      handleClose();
    }
  }, [countdown]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    onCancel();
  };

  const handleSubscriptionComplete = (result: { success: boolean }) => {
    if (result.success) {
      setSuccess(true);
      setCountdown(10);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {isChangingPlan ? 'Cambiar Plan' : 'Completar Suscripción'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Cerrar"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {success ? (
          <div className="text-center py-8">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">¡Pago Completado!</h3>
            <p className="text-gray-600 mb-6">
              Tu suscripción al plan {planName} ha sido activada correctamente.
            </p>
            <p className="text-sm text-gray-500">
              Esta ventana se cerrará automáticamente en {countdown} segundos...
            </p>
            <button
              onClick={handleClose}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Cerrar ahora
            </button>
          </div>
        ) : (
          <>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="font-medium text-gray-900 mb-1">Resumen de Suscripción</h3>
              <p className="text-gray-600 mb-2">Plan: {planName}</p>
              <p className="text-gray-600 font-medium">
                Precio: ${planPrice} USD/mes
              </p>
            </div>

            <StripeProvider>
              <CheckoutForm
                planId={planId}
                onSubscriptionComplete={handleSubscriptionComplete}
                buttonText={isChangingPlan ? 'Cambiar Plan' : 'Confirmar Suscripción'}
                isChangingPlan={isChangingPlan}
              />
            </StripeProvider>

            <button
              onClick={handleClose}
              className="mt-4 w-full flex justify-center items-center gap-2 py-2 text-gray-600 hover:text-gray-800"
            >
              <FaArrowLeft className="w-4 h-4" />
              Volver
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PlanCheckout; 